import { createContext, useCallback, useContext, useRef, useState } from 'react';

import { IconClose } from '../../icons';
import { useModalDialog } from '../../hooks/use-modal-dialog';

import './styles/confirm.css';

// Confirmación accesible que reemplaza a `window.confirm` (nativo: sin estilo, sin jerarquía
// de consecuencias, no distingue acciones destructivas). Provee un `confirm(opts) → Promise<boolean>`
// para que los call-sites cambien poco:  `if (!(await confirm({ … }))) return;`.
// El diálogo usa `useModalDialog` (foco/Escape/retorno) y enfoca "Cancelar" por defecto (seguro
// para acciones destructivas). Montar <ConfirmProvider> una vez, cerca de la raíz de la app.

const ConfirmContext = createContext(null);

export function useConfirm() {
    const confirm = useContext(ConfirmContext);
    if (!confirm) throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
    return confirm;
}

export function ConfirmProvider({ children }) {
    const [options, setOptions] = useState(null); // opciones del diálogo abierto, o null
    const resolveRef = useRef(null);

    const confirm = useCallback((opts = {}) => new Promise((resolve) => {
        resolveRef.current = resolve;
        setOptions(opts);
    }), []);

    const settle = useCallback((result) => {
        const resolve = resolveRef.current;
        resolveRef.current = null;
        setOptions(null);
        resolve?.(result);
    }, []);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {options && <ConfirmDialog options={options} onResolve={settle} />}
        </ConfirmContext.Provider>
    );
}

function ConfirmDialog({ options, onResolve }) {
    const {
        title = 'Confirmar acción',
        message,
        confirmLabel = 'Confirmar',
        cancelLabel = 'Cancelar',
        danger = false,
    } = options;
    const cardRef = useModalDialog(() => onResolve(false));

    return (
        <div className="confirm" role="dialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby={message ? 'confirm-message' : undefined}>
            <div className="confirm__overlay" onClick={() => onResolve(false)} aria-hidden="true" />
            <div className="confirm__card" ref={cardRef}>
                <header className="confirm__head">
                    <h2 className="confirm__title" id="confirm-title">{title}</h2>
                    <button type="button" className="confirm__close" onClick={() => onResolve(false)} aria-label="Cancelar"><IconClose /></button>
                </header>
                {message && <p className="confirm__message" id="confirm-message">{message}</p>}
                <footer className="confirm__actions">
                    <button type="button" className="confirm__btn confirm__btn--ghost" onClick={() => onResolve(false)}>{cancelLabel}</button>
                    <button type="button" className={`confirm__btn ${danger ? 'confirm__btn--danger' : 'confirm__btn--primary'}`} onClick={() => onResolve(true)}>{confirmLabel}</button>
                </footer>
            </div>
        </div>
    );
}
